import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ExternalLink, Eye, Loader2, Search, Plus, X, Building2, Landmark, CalendarDays,
  BookOpen, History, UserCog, Clock,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AuthorLink } from "@/components/social/AuthorLink";

// kind = stat key (businesses | capital | events | resources)
const KIND_META = {
  businesses: { icon: Building2, label: "Businesses", isOrg: true },
  capital: { icon: Landmark, label: "Capital profiles", isOrg: true },
  events: { icon: CalendarDays, label: "Events", isOrg: false },
  resources: { icon: BookOpen, label: "Resources", isOrg: false },
};
const STATUS_CLS = {
  published: "bg-teal-50 text-teal-700",
  draft: "bg-secondary text-muted-foreground",
  deleted: "bg-red-50 text-red-700",
  archived: "bg-amber-50 text-amber-700",
};
const liveHref = (ct, slug) => {
  if (!slug) return null;
  if (ct === "business") return `/b/${slug}`;
  if (ct === "capital") return `/capital/${slug}`;
  if (ct === "event") return `/event/${slug}`;
  return `/resource/${slug}`;
};
const initials = (s) => (s || "?").trim().slice(0, 2).toUpperCase();
const fmtFull = (s) =>
  s ? new Date(s).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }) : "—";
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

/** A name that links to /p/<slug> (plain text when no slug). */
function NameLink({ card, testId }) {
  return <AuthorLink card={{ display_name: card?.display_name, slug: card?.slug }} fallback="Member" className="font-[560] text-foreground" testId={testId} />;
}

function PersonRow({ card, role, onRemove }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2" data-testid={`role-row-${card.user_id}`}>
      <div className="flex min-w-0 items-center gap-2">
        <Avatar className="h-7 w-7">
          {card.headshot_url && <AvatarImage src={fileUrl(card.headshot_url)} alt="" />}
          <AvatarFallback className="text-[10px]">{initials(card.display_name)}</AvatarFallback>
        </Avatar>
        <NameLink card={card} testId={`role-name-${card.user_id}`} />
        {role && <Badge variant="secondary" className="shrink-0 text-[10px]">{role}</Badge>}
      </div>
      {onRemove && (
        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onRemove(card.user_id)} data-testid={`role-remove-${card.user_id}`} title="Remove">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

/** Search members by name / handle and pick one. */
function MemberSearch({ onPick, picked, testId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (picked) return;
    if (q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/member-search", { params: { q } });
        setResults(data.results || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q, picked]);

  if (picked) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid={`${testId}-picked`}>
        <div className="flex items-center gap-2">
          <Avatar className="h-7 w-7">
            {picked.headshot_url && <AvatarImage src={fileUrl(picked.headshot_url)} alt="" />}
            <AvatarFallback className="text-[10px]">{initials(picked.display_name)}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-[560] text-foreground">{picked.display_name}</span>
          {picked.handle && <span className="text-xs text-muted-foreground">@{picked.handle}</span>}
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onPick(null)} data-testid={`${testId}-clear`}><X className="h-4 w-4" /></Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-lg border border-input px-3">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or @handle…" className="border-0 px-0 focus-visible:ring-0" data-testid={`${testId}-input`} />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {results.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border bg-popover shadow-lg" data-testid={`${testId}-results`}>
          {results.map((r) => (
            <button key={r.user_id} type="button" onClick={() => { onPick(r); setQ(""); setResults([]); }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary" data-testid={`${testId}-result-${r.user_id}`}>
              <Avatar className="h-7 w-7">
                {r.headshot_url && <AvatarImage src={fileUrl(r.headshot_url)} alt="" />}
                <AvatarFallback className="text-[10px]">{initials(r.display_name)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-[540] text-foreground">{r.display_name}</span>
              {r.handle && <span className="text-xs text-muted-foreground">@{r.handle}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Full details card for one content item (modal). */
function ContentDetailModal({ open, onOpenChange, item }) {
  const [d, setD] = useState(null);
  const [history, setHistory] = useState([]);
  const [histMore, setHistMore] = useState(false);
  const [histOffset, setHistOffset] = useState(0);
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState("admin");
  const [busy, setBusy] = useState(false);

  const ct = item?.content_type;
  const isOrg = ct === "business" || ct === "capital";

  const loadHistory = useCallback(async (offset) => {
    const { data } = await api.get(`/admin/content/${ct}/${item.id}/history`, { params: { limit: 25, offset } });
    setHistory((prev) => (offset === 0 ? data.entries : [...prev, ...data.entries]));
    setHistMore(data.has_more); setHistOffset(offset + 25);
  }, [ct, item]);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/content/${ct}/${item.id}/detail`);
    setD(data);
  }, [ct, item]);

  useEffect(() => {
    if (!open || !item) return;
    setD(null); setHistory([]); setHistOffset(0); setPicked(null); setRole("admin");
    load();
    loadHistory(0);
  }, [open, item, load, loadHistory]);

  const addRole = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/content/${ct}/${item.id}/roles`, { user_id: picked.user_id, role });
      setD((p) => ({ ...p, admins: data.admins, editors: data.editors }));
      setPicked(null);
      toast.success(`${picked.display_name} added as ${role === "admin" ? "Admin" : "Editor"}.`);
      loadHistory(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign role"); }
    finally { setBusy(false); }
  };

  const removeRole = async (uid) => {
    try {
      const { data } = await api.delete(`/admin/content/${ct}/${item.id}/roles/${uid}`);
      setD((p) => ({ ...p, admins: data.admins, editors: data.editors }));
      toast.success("Member removed.");
      loadHistory(0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove member"); }
  };

  const href = d ? liveHref(ct, d.slug) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="content-detail-modal">
        <DialogHeader>
          <DialogTitle>Details</DialogTitle>
          <DialogDescription className="sr-only">Content details and change history</DialogDescription>
        </DialogHeader>
        {!d ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1" data-testid="content-detail-scroll">
            {/* Header */}
            <div className="flex items-start gap-3">
              {isOrg ? (
                <Avatar className="h-14 w-14 rounded-xl">
                  {d.image_url && <AvatarImage src={fileUrl(d.image_url)} alt="" />}
                  <AvatarFallback className="rounded-xl text-base">{initials(d.name)}</AvatarFallback>
                </Avatar>
              ) : d.image_url ? (
                <img src={fileUrl(d.image_url)} alt="" className="h-20 w-32 shrink-0 rounded-lg object-cover" data-testid="content-detail-cover" />
              ) : (
                <div className="flex h-20 w-32 shrink-0 items-center justify-center rounded-lg bg-secondary">
                  {ct === "event" ? <CalendarDays className="h-6 w-6 text-muted-foreground" /> : <BookOpen className="h-6 w-6 text-muted-foreground" />}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {href ? (
                    <a href={href} target="_blank" rel="noreferrer" className="text-lg font-[720] text-foreground hover:underline" data-testid="content-detail-name">{d.name}</a>
                  ) : (
                    <span className="text-lg font-[720] text-foreground" data-testid="content-detail-name">{d.name}</span>
                  )}
                  <Badge className={`${STATUS_CLS[d.status] || "bg-secondary"} text-[10px]`}>{d.status}</Badge>
                </div>
                {d.handle && <p className="text-sm text-muted-foreground">@{d.handle}</p>}
                {d.slug && <p className="text-xs text-muted-foreground">slug: <span className="font-mono">{d.slug}</span></p>}
                {d.category && <Badge variant="secondary" className="mt-1 text-[10px]" data-testid="content-detail-category">{d.category}</Badge>}
                {d.capital_type && <Badge variant="secondary" className="mt-1 text-[10px]" data-testid="content-detail-capital-type">{d.capital_type}</Badge>}
                {d.live_url && (
                  <a href={d.live_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-[540] text-primary hover:underline" data-testid="content-detail-viewlive">
                    <ExternalLink className="h-3.5 w-3.5" /> View live profile
                  </a>
                )}
              </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-secondary/40 p-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Owner</p>
                <NameLink card={d.owner} testId="content-detail-owner" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="text-foreground">{fmtDate(d.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Last updated</p>
                <p className="text-foreground">{fmtDate(d.updated_at)}</p>
              </div>
            </div>

            {/* Roles (orgs only) */}
            {isOrg && (
              <div className="space-y-3">
                <h4 className="flex items-center gap-2 text-sm font-[680] text-foreground"><UserCog className="h-4 w-4 text-muted-foreground" /> Profile admins & editors</h4>
                <div className="space-y-2">
                  <p className="text-xs font-[600] uppercase tracking-wide text-muted-foreground">Admins</p>
                  {(d.admins || []).length === 0 && <p className="text-sm text-muted-foreground">No admins.</p>}
                  {(d.admins || []).map((c) => <PersonRow key={c.user_id} card={c} role="Admin" onRemove={removeRole} />)}
                  <p className="mt-2 text-xs font-[600] uppercase tracking-wide text-muted-foreground">Editors</p>
                  {(d.editors || []).length === 0 && <p className="text-sm text-muted-foreground">No editors.</p>}
                  {(d.editors || []).map((c) => <PersonRow key={c.user_id} card={c} role="Editor" onRemove={removeRole} />)}
                </div>
                {/* Assign */}
                <div className="rounded-lg border border-dashed border-border p-3">
                  <p className="mb-2 text-xs font-[600] text-foreground">Assign a member</p>
                  <MemberSearch onPick={setPicked} picked={picked} testId="role-member-search" />
                  <div className="mt-2 flex items-center gap-2">
                    <Select value={role} onValueChange={setRole}>
                      <SelectTrigger className="w-32" data-testid="role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="contributor">Editor</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" disabled={!picked || busy} onClick={addRole} data-testid="role-assign-button">
                      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Assign
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Change history */}
            <div>
              <h4 className="flex items-center gap-2 text-sm font-[680] text-foreground"><History className="h-4 w-4 text-muted-foreground" /> Change history</h4>
              <div className="mt-2 max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="content-history-scroll">
                {history.length === 0 && <p className="text-sm text-muted-foreground">No changes recorded yet.</p>}
                {history.map((h) => (
                  <div key={h.id} className="rounded-lg border border-border/50 p-2.5 text-sm" data-testid={`history-${h.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <NameLink card={{ display_name: h.actor_name, slug: h.actor_slug }} testId={`history-actor-${h.id}`} />
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {fmtFull(h.created_at)}</span>
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {h.changes.map((c, i) => (
                        <li key={i} className="text-xs text-muted-foreground">
                          <span className="font-[560] text-foreground">{c.label}</span>
                          {c.from || c.to ? (
                            <> — <span className="line-through opacity-70">{c.from || "(empty)"}</span> → <span className="text-foreground">{c.to || "(empty)"}</span></>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {histMore && <Button variant="ghost" size="sm" className="w-full" onClick={() => loadHistory(histOffset)} data-testid="history-load-more">Load more</Button>}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** List of a member's content (modal). Click the eye to open the details card. */
export function ContentListModal({ open, onOpenChange, uid, kind }) {
  const meta = KIND_META[kind] || {};
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !uid || !kind) return;
    setItems(null);
    api.get(`/admin/users/${uid}/content/${kind}`)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, [open, uid, kind]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl" data-testid="content-list-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {meta.icon && <meta.icon className="h-5 w-5 text-muted-foreground" />} {meta.label}
            </DialogTitle>
            <DialogDescription>Owned by this member. Click the eye to view full details.</DialogDescription>
          </DialogHeader>
          {items === null ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="content-list-empty">No {meta.label?.toLowerCase()} yet.</p>
          ) : (
            <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1" data-testid="content-list-scroll">
              {items.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2" data-testid={`content-row-${it.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    {meta.isOrg && (
                      <Avatar className="h-9 w-9 rounded-lg">
                        {it.image_url && <AvatarImage src={fileUrl(it.image_url)} alt="" />}
                        <AvatarFallback className="rounded-lg text-xs">{initials(it.name)}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-[560] text-foreground" data-testid={`content-row-name-${it.id}`}>{it.name}</p>
                      <Badge className={`${STATUS_CLS[it.status] || "bg-secondary"} text-[10px]`}>{it.status}</Badge>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {it.live_url ? (
                      <a href={it.live_url} target="_blank" rel="noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary hover:bg-secondary" title="Open live profile" data-testid={`content-row-live-${it.id}`}>
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    ) : (
                      <span className="inline-flex h-8 w-8 items-center justify-center text-muted-foreground/40" title="Not published"><ExternalLink className="h-4 w-4" /></span>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDetail(it)} title="View details" data-testid={`content-row-details-${it.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ContentDetailModal open={!!detail} onOpenChange={(o) => !o && setDetail(null)} item={detail} />
    </>
  );
}
